import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import SmoothScroll from "@/components/SmoothScroll";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import ServicesIndex from "@/pages/ServicesIndex";
import ServicePage from "@/pages/ServicePage";
import { EASE } from "@/lib/ui";
import { LanguageProvider } from "@/i18n/lang";

const Loader = () => (
  <motion.div
    className="fixed inset-0 z-[100] bg-ink flex items-center justify-center"
    initial={{ opacity: 1 }}
    exit={{ y: "-100%" }}
    transition={{ duration: 1, ease: EASE }}
  >
    <div className="text-center overflow-hidden">
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
        className="flex items-center gap-2 font-serif text-5xl md:text-6xl"
      >
        <span className="text-white">Glamour</span>
        <span className="gold-text italic">Dent</span>
      </motion.div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.4 }}
        className="mt-5 h-px bg-gradient-to-r from-transparent via-gold to-transparent origin-left"
      />
    </div>
  </motion.div>
);

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/services" element={<PageTransition><ServicesIndex /></PageTransition>} />
        <Route path="/services/:slug" element={<PageTransition><ServicePage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const skipFx = typeof window !== "undefined" && window.location.search.includes("nofx");
  const [loading, setLoading] = useState(!skipFx);
  useEffect(() => {
    if (skipFx) return;
    const t = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(t);
  }, [skipFx]);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AnimatePresence>{loading && <Loader key="loader" />}</AnimatePresence>
          <SmoothScroll>
            <Navbar />
            <main>
              <AnimatedRoutes />
            </main>
            <Footer />
          </SmoothScroll>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
